import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Blocks, CalendarClock, CalendarDays, HardDrive, CreditCard } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import GoogleWorkspacePanel from "@/components/GoogleWorkspacePanel";
import PersonalCalendarPanel from "@/components/PersonalCalendarPanel";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel } from "@/components/ui/alert-dialog";

const ICONS = { mlabs: CalendarClock, google_agenda: CalendarDays, google_drive: HardDrive, gateway: CreditCard };

const STATUS_STYLE = {
  conectado: "text-[#10B981] border-[#10B981]/30 bg-[#10B981]/10",
  desconectado: "text-[#71717A] border-[#27272A]",
};

export default function Integracoes() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [lista, setLista] = useState([]);
  const [google, setGoogle] = useState(null);
  const [error, setError] = useState("");
  const [disconnectOpen, setDisconnectOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => {
    setError("");
    Promise.all([api.get("/integracoes"), api.get("/integracoes/google/status")])
      .then(([list, status]) => { setLista(list.data); setGoogle(status.data); })
      .catch((err) => setError(apiError(err)));
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (["conectado", "cancelado", "erro"].includes(searchParams.get("google"))) {
      if (searchParams.get("google") === "conectado") toast.success("Google conectado. Nenhum convite foi enviado.");
      else if (searchParams.get("google") === "erro") toast.error(searchParams.get("motivo") || "Não foi possível conectar o Google. Tente novamente.");
      else toast.info("Autorização cancelada. Nenhuma conexão foi alterada.");
      const next = new URLSearchParams(searchParams);
      next.delete("google");
      next.delete("motivo");
      next.set("tab", "integracoes");
      setSearchParams(next, { replace: true });
      load();
    }
  }, [searchParams, setSearchParams]);

  const alternar = async (i) => {
    const novo = i.status === "conectado" ? "desconectado" : "conectado";
    try {
      await api.patch(`/integracoes/${i.key}`, { status: novo });
    } catch (err) {
      toast.error(apiError(err));
      return;
    }
    toast.success(novo === "conectado" ? `${i.nome} conectado (modo preparado)` : `${i.nome} desconectado`);
    load();
  };

  const conectarGoogle = async () => {
    setBusy(true);
    try {
      const res = await api.get("/oauth/google/connect");
      window.location.href = res.data.authorization_url;
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const desconectarGoogle = async () => {
    setBusy(true);
    try {
      await api.delete("/integracoes/google/desconectar");
      setDisconnectOpen(false); setGoogle(null);
      toast.success("Google desconectado do Gestão. Eventos e arquivos foram preservados.");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const acao = (i) => {
    if (i.key === "google_agenda" || i.key === "google_drive") {
      if (google?.conectado)
        return <Button data-testid={`integracao-toggle-${i.key}`} disabled={busy || !google.pode_consultar} onClick={() => setDisconnectOpen(true)} variant="outline" className="mt-5 rounded-full border-[#F43F5E]/40 text-[#F43F5E] hover:bg-[#F43F5E]/10">Desconectar</Button>;
      if (google?.configurado)
        return <Button data-testid={`integracao-toggle-${i.key}`} disabled={busy || !google.pode_configurar} onClick={conectarGoogle} variant="outline" className="mt-5 rounded-full border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10">{google.reautorizar ? "Reconectar Google" : "Conectar Google"}</Button>;
      return <Button data-testid={`integracao-toggle-${i.key}`} disabled variant="outline" className="mt-5 rounded-full border-[#27272A] text-[#71717A]">Aguardando credenciais</Button>;
    }
    if (i.key === "mlabs")
      return <Button data-testid={`integracao-toggle-${i.key}`} disabled variant="outline" title="A mLabs não publica API — o link de aprovação manual por postagem segue ativo"
        className="mt-5 rounded-full border-[#27272A] text-[#71717A]">Fluxo manual ativo</Button>;
    const conectado = i.status === "conectado";
    return (
      <Button data-testid={`integracao-toggle-${i.key}`} onClick={() => alternar(i)} variant="outline"
        disabled={!isAdmin} title={isAdmin ? undefined : "Somente administradores alteram integrações"}
        className={`mt-5 rounded-full ${conectado ? "border-[#F43F5E]/40 text-[#F43F5E] hover:bg-[#F43F5E]/10" : "border-[#8CC63F]/40 text-[#8CC63F] hover:bg-[#8CC63F]/10"}`}>
        {conectado ? "Desconectar" : "Conectar"}
      </Button>
    );
  };

  return (
    <div className="space-y-6" data-testid="integracoes-page">
      <PersonalCalendarPanel />
      {error && <div role="alert" className="space-y-2"><p>{error}</p><Button onClick={load} variant="outline">Tentar novamente</Button></div>}
      <h2 className="text-xl font-semibold">Conexões do estúdio</h2>
      <p className="text-sm text-[#A1A1AA] max-w-xl">A conexão institucional de Google Agenda e Drive abaixo continua separada da sua agenda individual. A mLabs opera com link de aprovação manual até a API oficial ser liberada.</p>
      {google?.conectado && <p className="font-mono text-xs text-[#10B981]" data-testid="google-conta">Google conectado como {google.email}</p>}

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {lista.map((i) => {
          const Icon = ICONS[i.key] || Blocks;
          const conectado = i.status === "conectado";
          return (
            <div key={i.key} data-testid={`integracao-card-${i.key}`} className="studio-card studio-card-hover p-6 flex flex-col">
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-[#8CC63F]/12 border border-[#8CC63F]/30 flex items-center justify-center">
                  <Icon size={20} className="text-[#8CC63F]" strokeWidth={1.8} />
                </div>
                <span data-testid={`integracao-status-${i.key}`} className={`font-mono text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${STATUS_STYLE[i.status] || STATUS_STYLE.desconectado}`}>
                  {conectado ? "conectado" : "desconectado"}
                </span>
              </div>
              <p className="text-base font-medium text-[#F4F4F0] mt-5">{i.nome}</p>
              <p className="caption-label mt-1">{i.categoria}</p>
              <p className="text-sm text-[#A1A1AA] mt-3 flex-1">{i.descricao}</p>
              {acao(i)}
            </div>
          );
        })}
      </div>
      <GoogleWorkspacePanel google={google} reload={load} />
      <AlertDialog open={disconnectOpen} onOpenChange={setDisconnectOpen}><AlertDialogContent>
        <AlertDialogHeader><AlertDialogTitle>Desconectar Google?</AlertDialogTitle>
          <AlertDialogDescription>O Gestão deixará de acessar Agenda e Drive. Eventos e arquivos existentes não serão apagados. Para revogar também a autorização no Google, use as configurações de segurança da sua conta.</AlertDialogDescription></AlertDialogHeader>
        <AlertDialogFooter><AlertDialogCancel disabled={busy}>Voltar</AlertDialogCancel><Button disabled={busy} onClick={desconectarGoogle}>Desconectar do Gestão</Button></AlertDialogFooter>
      </AlertDialogContent></AlertDialog>
    </div>
  );
}
